import {
  Box,
  Flex,
  Input,
  MenuGroup,
  MenuItem,
  Select,
  Spacer,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  DragEventHandler,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { useFormContext } from 'react-hook-form';

import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import {
  CellContext,
  Column,
  Row,
  Table,
  TableMeta,
  flexRender,
} from '@tanstack/react-table';
import { isEmpty, isNil, round } from 'lodash';
import invariant from 'tiny-invariant';
import { useTranslation } from 'react-i18next';
import { EventType, Unit, UnitSettingsType } from '../../gql/graphql';
import { formatUnit } from '../../utils/unit-utils';
import SparkelIcon from '../common/icon/SparkelIcon';
import {
  TableEntry as OriginalTableEntry,
  getEntryDiffPath,
} from '../project/order-entries-panel/OrderEntriesPanel';
import { useProjectHistory } from '../project/project-history/hooks/useProjectHistory';
import { useDiffing } from '../project/project-history/hooks/useDiffing';
import { DiffBadge } from '../project/project-history/DiffBadge';
import { DiffText } from '../project/project-history/DiffText';
import DiffNumber from '../project/project-history/DiffNumber';
import { flexProps, getColumnName } from './table-utils';
import TableFooter from './TableFooter';
import { TableHeader } from './TableHeader';

export interface TableEntry {
  name?: string | null;
}
interface EditableRowFormValues {
  id: string;
}
interface EditableRowsFormValues {
  [rowId: string]: EditableRowFormValues;
}

type SparkelTableProps<D extends TableEntry> = {
  tableInstance: Table<D>;
  newRowRef?: React.MutableRefObject<HTMLDivElement | null>;
  newRowId?: string;
  onRowDoubleClick: (row: Row<D>) => void;
  onColumnRename: (columnId: string, name: string) => void;
  onColumnDrop?: (columnId: string) => DragEventHandler;
  existingColumnNames: string[];
  onConfirmColumnDrop: (isSum: boolean) => void;
  activeDropColumnData: {
    columnId: string;
    property: {
      category: string;
      propertyName: string;
      displayName: string;
    };
  } | null;
  closeColumnDropConfirmation: () => void;
  readOnly: boolean;
  updating: boolean;
  children: React.ReactNode;
  showFooter: boolean;
};

export type EditableTableMeta = {
  isEditing: boolean;
  // id's of rows that are currently being edited
  editingRows: string[];
};

export function isEditableTableMeta(
  meta: TableMeta<TableEntry> | undefined
): meta is EditableTableMeta {
  return (meta as EditableTableMeta).editingRows !== undefined;
}

export type SparkelTableColumnMeta = {
  isNumeric?: boolean;
  isRequired?: boolean;
  canDrop?: boolean;
  menuItems?:
    | React.ReactNode
    | (<D extends TableEntry>(table: Table<D>) => React.ReactNode | null);
};

export default function SparkelTable<D extends OriginalTableEntry>({
  tableInstance,
  onRowDoubleClick,
  onColumnRename,
  onColumnDrop,
  existingColumnNames,
  activeDropColumnData,
  onConfirmColumnDrop,
  closeColumnDropConfirmation,
  newRowRef,
  newRowId,
  readOnly,
  updating,
  children,
  showFooter,
}: SparkelTableProps<D>) {
  const textColor = useColorModeValue('gray.400', 'gray.500');
  const hoverColor = useColorModeValue('gray.50', 'gray.700');

  const backgroundColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const editingOtherColor = useColorModeValue('gray.200', 'gray.600');
  const tableMeta = tableInstance.options.meta;
  const rows = tableInstance.getRowModel().rows;
  invariant(isEditableTableMeta(tableMeta), 'Table meta must be defined');

  // This is just for minimum touching of code, we'll move this around later once things have matured
  const { isHistoryDrawerOpen, referenceState, diff } = useProjectHistory();
  const { getTextColor, getBackgroundColor } = useDiffing();
  /* eslint-disable complexity */
  const getEntryEventType = useCallback(
    (row: Row<OriginalTableEntry>) => {
      const entryId = row.original.id;
      const orderId = row.original.orderId;
      if (isHistoryDrawerOpen && referenceState && diff && orderId) {
        const collectionKey = `project.orders.${orderId}.orderEntries`;

        const collectionDiff = diff[collectionKey];
        if (collectionDiff) {
          if (
            collectionDiff.new &&
            (!collectionDiff.old || collectionDiff.old === '[]') &&
            collectionDiff.new?.[entryId]
          ) {
            return EventType.Created;
          } else if (
            (!collectionDiff.new || collectionDiff.new === '[]') &&
            collectionDiff.old &&
            collectionDiff.old?.[entryId]
          ) {
            return EventType.Deleted;
          }
        }

        // We probably need to extract this out into its own util for reuse?
        let entryKey = `${collectionKey}.${entryId}`;
        let subrowsCollectionKey = entryKey;
        const keySpace = row.id.split('.');
        for (let i = 2; i <= keySpace.length; i++) {
          const cKey = keySpace.slice(0, i).join('.');
          const found = rows.find((cRow) => cRow.id === cKey);
          if (found) {
            // we should also have a collection diff for every level
            const subKey = `.subRows.${found.original.name}`;
            entryKey += subKey;
            if (i < keySpace.length) {
              subrowsCollectionKey += subKey;
            }
          }
        }
        subrowsCollectionKey += '.subRows';
        const subrowsCollectionDiff = diff[subrowsCollectionKey];
        if (subrowsCollectionDiff) {
          const nameId = row.original.name;
          if (
            subrowsCollectionDiff.new &&
            (!subrowsCollectionDiff.old ||
              subrowsCollectionDiff.old === '[]') &&
            subrowsCollectionDiff.new?.[nameId]
          ) {
            return EventType.Created;
          } else if (
            (!subrowsCollectionDiff.new ||
              subrowsCollectionDiff.new === '[]') &&
            subrowsCollectionDiff.old &&
            subrowsCollectionDiff.old?.[nameId]
          ) {
            return EventType.Deleted;
          }
        }

        const entryDiff = diff[entryKey];
        if (entryDiff) {
          if (entryDiff.new && (!entryDiff.old || entryDiff.old === '[]')) {
            return EventType.Created;
          } else if (
            (!entryDiff.new || entryDiff.new === '[]') &&
            entryDiff.old
          ) {
            return EventType.Deleted;
          }
        }

        // If any of our ancestors has been marked as either having been created, we also need to be marked similarly
        for (const [key, val] of Object.entries(diff)) {
          if (!entryKey.includes(key) || entryKey === key) continue;
          if (val.new && (!val.old || val.old === '[]')) {
            return EventType.Created;
          } else if ((!val.new || val.new === '[]') && val.old) {
            return EventType.Deleted;
          }
        }

        // we need to ensure that all the quantites that can have rounding errors are taken care of
        // TODO: Check whether arrays with the nulls will require some special attention here
        const hasOtherChanges = Object.entries(diff).some(([key, value]) => {
          if (key.startsWith(entryKey)) {
            const oldValue = value.old;
            const newValue = value.new;
            if (typeof oldValue === 'number' && typeof newValue === 'number') {
              return round(oldValue, 2) !== round(newValue, 2);
            }
            if (
              key.includes('.subRows') &&
              key.includes('elementIdentifiersDbid')
            ) {
              return false;
            }
            return true;
          }
          return false;
        });
        return hasOtherChanges ? EventType.Modified : undefined;
      }
    },
    [isHistoryDrawerOpen, referenceState, diff, rows]
  );

  return (
    <Flex
      role="table"
      direction="column"
      align="stretch"
      grow="1"
      pointerEvents={updating ? 'none' : undefined}
      transition="opacity 0.2s ease-in-out"
      opacity={updating ? 0.2 : 1}
      sx={{
        '[data-droptarget="true"]': {
          backgroundColor: hoverColor,
        },
      }}
      mx={-2}
    >
      <Flex
        role="row"
        alignItems="end"
        fontSize="xs"
        position="sticky"
        top={0}
        zIndex={3}
      >
        {tableInstance
          .getHeaderGroups()
          .map((headerGroup) =>
            headerGroup.headers.map((header) => (
              <TableHeader
                key={header.id}
                header={header}
                onColumnRename={onColumnRename}
                onColumnDrop={onColumnDrop}
                activeDropColumnData={activeDropColumnData}
                closeColumnDropConfirmation={closeColumnDropConfirmation}
                backgroundColor={backgroundColor}
                textColor={textColor}
                borderColor={borderColor}
                hoverColor={hoverColor}
                tableInstance={tableInstance}
                onConfirmColumnDrop={onConfirmColumnDrop}
                existingColumnNames={existingColumnNames}
              />
            ))
          )}
      </Flex>
      <Flex
        role="rowgroup"
        direction="column"
        justifyContent="flex-start"
        overflowY={'auto'}
      >
        {tableInstance.getRowModel().rows.map((row) => {
          const handleRowClicked: MouseEventHandler = (e) => {
            if (tableMeta.isEditing) {
              return;
            }

            if (e.ctrlKey || e.metaKey || e.shiftKey) {
              row.toggleSelected();
            } else {
              tableInstance.setRowSelection({
                [row.id]: true,
                ...Object.fromEntries(
                  row.subRows.map((subRow) => [subRow.id, true])
                ),
              });
            }
          };

          const isEditingThisRow = isEditingRow(row, tableMeta);
          const isEditingOtherRow = tableMeta.isEditing && !isEditingThisRow;

          const eventType = getEntryEventType(row as Row<OriginalTableEntry>);
          const rowTxtColor = eventType ? getTextColor(eventType) : undefined;
          const rowBgColor = eventType
            ? getBackgroundColor(eventType)
            : undefined;

          return (
            <Flex
              key={row.id}
              role="row"
              alignItems="flex-start"
              maxHeight={8}
              backgroundColor={rowBgColor}
              textColor={rowTxtColor}
              color={isEditingOtherRow ? editingOtherColor : undefined}
              _hover={{
                backgroundColor: hoverColor,
              }}
              borderRadius={'lg'}
              fontWeight={row.getIsSelected() ? 'bold' : undefined}
              pointerEvents={isEditingOtherRow ? 'none' : undefined}
              cursor="pointer"
              onClick={handleRowClicked}
              onDoubleClick={
                tableMeta.isEditing || row.depth !== 0 || readOnly
                  ? undefined
                  : () => void onRowDoubleClick(row)
              }
              ref={newRowRef && row.id === newRowId ? newRowRef : undefined}
              position="relative"
            >
              {row.getVisibleCells().map((cell) => (
                <Box
                  key={cell.id}
                  role="cell"
                  {...flexProps(cell.column)}
                  height="100%"
                  display="flex"
                  justifyContent="center"
                  flexDir={'column'}
                  padding={1}
                  paddingLeft={2}
                  fontSize="xs"
                  textAlign={
                    (cell.column.columnDef.meta as SparkelTableColumnMeta)
                      ?.isNumeric
                      ? 'end'
                      : cell.column.id === 'edit'
                      ? 'center'
                      : undefined
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Box>
              ))}
              {eventType && isHistoryDrawerOpen ? (
                <Box
                  alignItems="center"
                  height="100%"
                  display="flex"
                  position="absolute"
                  right={2}
                >
                  <DiffBadge eventType={eventType} />
                </Box>
              ) : null}
            </Flex>
          );
        })}
      </Flex>

      {children}
      <Spacer />
      {showFooter ? (
        <Flex
          role="row"
          alignItems="flex-end"
          fontSize="xs"
          bottom={0}
          zIndex={3}
          borderTop={'1px'}
          borderTopColor={borderColor}
          height={8}
        >
          {tableInstance
            .getFooterGroups()
            .map((footerGroup) =>
              footerGroup.headers.map((footer) => (
                <TableFooter
                  key={footer.id}
                  header={footer}
                  backgroundColor={backgroundColor}
                  textColor={textColor}
                  borderColor={borderColor}
                  hoverColor={hoverColor}
                  tableInstance={tableInstance}
                />
              ))
            )}
        </Flex>
      ) : undefined}
    </Flex>
  );
}

export function HeaderText({
  isNumeric,
  marginLeft = 1,
  children,
}: {
  isNumeric?: boolean;
  marginLeft?: number;
  children: React.ReactNode;
}) {
  return (
    <Text
      as={'span'}
      marginRight={2}
      marginLeft={marginLeft}
      noOfLines={1}
      wordBreak="break-all"
      textAlign={isNumeric ? 'end' : 'start'}
    >
      {children}
    </Text>
  );
}

export function isEditingRow<D extends TableEntry>(
  row: Row<D>,
  tableMeta: EditableTableMeta
) {
  return tableMeta.editingRows.includes(row.id);
}

export function SortableColumnMenuItems<D extends TableEntry>({
  column,
}: {
  column: Column<D, unknown>;
}): React.ReactElement {
  const { t } = useTranslation('table');
  return (
    <MenuGroup>
      <MenuItem
        onClick={() => column.toggleSorting(false)}
        icon={<SparkelIcon icon={faArrowUp} fixedWidth />}
      >
        {t('column.sort-ascending')}
      </MenuItem>
      <MenuItem
        onClick={() => column.toggleSorting(true)}
        icon={<SparkelIcon icon={faArrowDown} fixedWidth />}
      >
        {t('column.sort-descending')}
      </MenuItem>
    </MenuGroup>
  );
}

export function UnitPriceCell(
  props: CellContext<OriginalTableEntry, string | number | null>
): React.ReactElement {
  const { isHistoryDrawerOpen, diff } = useProjectHistory();
  const { table, row } = props;
  const rows = table.getRowModel().rows;
  const orderId = row.original.orderId;
  const entryId = row.original.id;
  const unitPriceDiff = useMemo(() => {
    if (!isHistoryDrawerOpen || !diff) return;
    let diffKey = `project.orders.${orderId}.orderEntries.${entryId}`;
    const subKey = getEntryDiffPath(rows, row.id);
    diffKey += `${subKey}.unitPrice`;
    const unitPriceDiff = diff[diffKey];
    if (unitPriceDiff) {
      return {
        old: unitPriceDiff.old || 0,
        new: unitPriceDiff.new || 0,
      };
    }
  }, [isHistoryDrawerOpen, diff, rows, orderId, entryId, row.id]);
  if (unitPriceDiff) {
    return (
      <Box alignSelf="flex-end" paddingRight={2}>
        <DiffNumber newValue={unitPriceDiff.new} oldValue={unitPriceDiff.old} />
      </Box>
    );
  }
  return <EditableCell {...{ ...props }} />;
}

export function EditableCell<D extends TableEntry>({
  column,
  table: {
    options: { meta: tableMeta },
  },
  row,
  getValue,
}: CellContext<D, string | number | null>): React.ReactElement {
  const {
    register,
    formState: { errors },
    getValues,
  } = useFormContext<EditableRowsFormValues>();
  invariant(isEditableTableMeta(tableMeta), 'Table meta must be defined');
  const columnMeta = (column.columnDef.meta ?? {}) as SparkelTableColumnMeta;
  if (isEditingRow(row, tableMeta)) {
    const columnId = column.id as keyof EditableRowFormValues;
    const columnName = getColumnName(column);
    const autoFocus =
      // NOTE: If we change the id of the name column we must change this check
      column.id === 'name' && Object.keys(getValues()).sort()[0] === row.id;

    return (
      <Input
        {...register(`${row.id}.${columnId}`, {
          required: columnMeta?.isRequired,
          valueAsNumber: columnMeta?.isNumeric,
        })}
        aria-label={columnName}
        type={columnMeta?.isNumeric ? 'number' : 'text'}
        step="0.01"
        isInvalid={!!errors?.[row.id]?.[columnId]}
        textAlign={'start'}
        form="edit-table-entry-form"
        size="sm"
        px={1}
        borderRadius={0}
        borderColor="chakra-border-color"
        autoFocus={autoFocus}
        onFocus={(e) => (autoFocus ? e.target.select() : undefined)}
        placeholder={columnName}
      />
    );
  } else {
    return (
      <Text
        as="span"
        aria-label={`${row.original.name}-${column.id}`}
        noOfLines={1}
        wordBreak="break-all"
      >
        {getValue()}
      </Text>
    );
  }
}

export function TextCell<D extends TableEntry>({
  row,
  column,
  getValue,
}: CellContext<D, string | number | undefined>): React.ReactElement {
  const value = getValue();
  return (
    <Text
      as="span"
      aria-label={`${row.original.name}-${column.id}`}
      noOfLines={
        typeof value === 'string' && value.includes('\n') ? undefined : 1
      }
      whiteSpace="pre-line"
      wordBreak="break-all"
    >
      {!isNil(value) && !isEmpty(value) ? value : <span>&nbsp;</span>}
    </Text>
  );
}

export type NumericCellMeta = {
  numberFormatOptions?: Intl.NumberFormatOptions;
};
export function NumericCell<D extends TableEntry>({
  row,
  column,
  getValue,
}: CellContext<D, number>): React.ReactElement {
  const locale = navigator.language || 'nb-NO';
  const numberFormatOptions = (column.columnDef.meta as NumericCellMeta)
    ?.numberFormatOptions;
  const value = getValue();

  return (
    <Text
      as="span"
      aria-label={`${row.original.name}-${column.id}`}
      noOfLines={1}
      whiteSpace="pre-line"
      wordBreak="break-all"
    >
      {value.toLocaleString(locale, numberFormatOptions)}
    </Text>
  );
}

export function PriceCell<D extends OriginalTableEntry>(
  props: CellContext<D, number>
): React.ReactElement {
  const numericCellProps: typeof props = {
    ...props,
    column: {
      ...props.column,
      columnDef: {
        ...props.column.columnDef,
        meta: {
          ...props.column.columnDef.meta,
          numberFormatOptions: { minimumFractionDigits: 2 },
        } as NumericCellMeta,
      },
    },
  };

  const { isHistoryDrawerOpen, diff } = useProjectHistory();
  const { table, row } = props;
  const rows = table.getRowModel().rows;
  const orderId = row.original.orderId;
  const entryId = row.original.id;
  const totalDiff = useMemo(() => {
    if (!isHistoryDrawerOpen || !diff) return;
    let commonKey = `project.orders.${orderId}.orderEntries.${entryId}`;
    const subKey = getEntryDiffPath(rows as Row<OriginalTableEntry>[], row.id);
    commonKey += subKey;
    const unitPriceKey = `${commonKey}.unitPrice`;
    const quantityKey = `${commonKey}.resolvedQuantity.resolvedValue.value`;
    let oldQtyVal =
      typeof row.original.resolvedQuantity?.value === 'number'
        ? row.original.resolvedQuantity?.value
        : 0;
    let oldUnitPrice = row.original.unitPrice ?? 0;
    let newQtyVal = oldQtyVal;
    let newUnitPrice = oldUnitPrice;
    const unitPriceDiff = diff[unitPriceKey];
    if (unitPriceDiff) {
      newUnitPrice = unitPriceDiff.new ?? 0;
      oldUnitPrice = unitPriceDiff.old ?? 0;
    }
    const qtyDiff = diff[quantityKey];
    if (qtyDiff) {
      newQtyVal = qtyDiff.new ?? 0;
      oldQtyVal = qtyDiff.old ?? 0;
    }
    return {
      old: oldQtyVal * oldUnitPrice,
      new: newQtyVal * newUnitPrice,
    };
  }, [
    isHistoryDrawerOpen,
    diff,
    rows,
    orderId,
    entryId,
    row.id,
    row.original.unitPrice,
    row.original.resolvedQuantity?.value,
  ]);

  if (totalDiff) {
    return (
      <Box alignSelf="flex-end" paddingRight={2}>
        <DiffNumber newValue={totalDiff.new} oldValue={totalDiff.old} />
      </Box>
    );
  }

  return <NumericCell {...numericCellProps} />;
}

export type UnitColumnMeta = {
  unitSettings?: UnitSettingsType;
  forceUnit?: (row: OriginalTableEntry) => boolean;
};

export function UnitCell({
  row,
  column,
  getValue,
  table,
}: CellContext<OriginalTableEntry, Unit | null | undefined>): ReactNode {
  const { register } = useFormContext<EditableRowsFormValues>();
  const {
    options: { meta },
  } = table;

  invariant(isEditableTableMeta(meta), 'Table meta is not defined');

  const columnMeta = column.columnDef.meta;

  const rowAsOrderEntryWithId = { ...row.original, id: row.id };

  const { isHistoryDrawerOpen, diff } = useProjectHistory();
  const orderId = row.original.orderId;
  const entryId = row.original.id;
  const rows = table.getRowModel().rows;

  const unitDiff = useMemo(() => {
    if (!isHistoryDrawerOpen || !diff) return;
    const subKey = getEntryDiffPath(rows, row.id);
    const entryKey = `project.orders.${orderId}.orderEntries.${entryId}${subKey}.unit`;
    return diff[entryKey];
  }, [isHistoryDrawerOpen, diff, row, orderId, entryId, rows]);

  if (isHistoryDrawerOpen && unitDiff?.old && unitDiff?.new) {
    return (
      <DiffText
        oldValue={formatUnit(unitDiff?.old)}
        newValue={formatUnit(unitDiff.new)}
      />
    );
  }

  if (meta.editingRows.includes(row.id)) {
    return (
      <Select
        {...register(`${row.id}.${column.id as keyof EditableRowFormValues}`)}
        aria-label={`${row.original.name}-${column.id}`}
        size="xs"
        borderRadius={0}
        isDisabled={(columnMeta as UnitColumnMeta).forceUnit?.(
          rowAsOrderEntryWithId
        )}
      >
        {Object.entries(Unit).map(([key, value]) => (
          <option key={value} value={value}>
            {formatUnit(key)}
          </option>
        ))}
      </Select>
    );
  } else {
    const value = getValue();
    const formattedValue = formatUnit(value);
    return formattedValue;
  }
}
